import React, { useState, useEffect } from "react";

import { FaChevronDown, FaChevronUp, FaBuilding } from "react-icons/fa";
import { getIndustryFaIcon } from "../../utils/iconHelper";

const Categories = ({
  availableCategories = [],
  availableSubCategories = [],
  selectedCategory,
  onCategorySelect,
  selectedSubCategory,
  onSubCategorySelect,
  baseReports = [],
  isFiltered,
  onClearAll,
}) => {
  const getCategoryCount = (catName) => {
    return baseReports.filter((r) => {
      const inds = [r.category, r.industry, r.Industry]
        .filter(Boolean)
        .flatMap((s) =>
          typeof s === "string" ? [s.replace(/\s+/g, " ").trim()] : Array.isArray(s) ? s : [s],
        );
      return inds.some((ind) => ind.toLowerCase() === catName.toLowerCase());
    }).length;
  };

  const getSubCategoryCount = (catName, subName) => {
    return baseReports.filter((r) => {
      const inds = [r.category, r.industry, r.Industry]
        .filter(Boolean)
        .flatMap((s) =>
          typeof s === "string" ? [s.replace(/\s+/g, " ").trim()] : Array.isArray(s) ? s : [s],
        );
      const subs = [r.subCategory, r.sub_category, r.sub_industry]
        .filter(Boolean)
        .flatMap((s) =>
          typeof s === "string" ? [s.replace(/\s+/g, " ").trim()] : Array.isArray(s) ? s : [s],
        );
      const matchesCat = inds.some(
        (ind) => ind.toLowerCase() === catName.toLowerCase(),
      );
      const matchesSub = subs.some(
        (sub) => sub.toLowerCase() === subName.toLowerCase(),
      );
      return matchesCat && matchesSub;
    }).length;
  };

  const [openIds, setOpenIds] = useState([]);

  const dynamicHierarchy = React.useMemo(() => {
    const hierarchy = {};

    baseReports.forEach((r) => {
      const inds = [r.category, r.industry, r.Industry]
        .filter(Boolean)
        .flatMap((s) => (typeof s === "string" ? [s.replace(/\s+/g, " ").trim()] : [s]));
      const subs = [r.subCategory, r.sub_category, r.sub_industry]
        .filter(Boolean)
        .flatMap((s) => (typeof s === "string" ? [s.replace(/\s+/g, " ").trim()] : [s]));

      inds.forEach((ind) => {
        const key = ind.toLowerCase();
        if (!hierarchy[key]) {
          hierarchy[key] = {
            id: "dyn-" + ind,
            name: ind,
            icon: null, // We use getIndustryFaIcon inline later
            subCategories: new Set(),
          };
        }
        subs.forEach((sub) => hierarchy[key].subCategories.add(sub));
      });
    });

    return Object.values(hierarchy)
      .map((c) => ({ ...c, subCategories: Array.from(c.subCategories).sort() }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [baseReports]);

  // Sync openIds when selectedCategory changes from outside (e.g. URL param)
  useEffect(() => {
    if (selectedCategory) {
      const match = dynamicHierarchy.find((c) => c.name === selectedCategory);
      if (match) {
        setOpenIds((prev) =>
          prev.includes(match.id) ? prev : [...prev, match.id],
        );
      }
    }
  }, [selectedCategory, dynamicHierarchy]);

  const handleToggleAccordion = (e, cat) => {
    e.stopPropagation();
    setOpenIds((prev) =>
      prev.includes(cat.id)
        ? prev.filter((id) => id !== cat.id)
        : [...prev, cat.id],
    );
  };

  const handleCategoryFilter = (cat) => {
    const isActive = selectedCategory === cat.name;
    if (isActive) {
      onCategorySelect(null);
    } else {
      onCategorySelect(cat.name);
      setOpenIds((prev) => (prev.includes(cat.id) ? prev : [...prev, cat.id]));
    }
    onSubCategorySelect(null);
  };

  const visibleCategories = dynamicHierarchy;

  return (
    <aside className="d-flex flex-column gap-4">
      {/* Categories List */}
      <div className="categories-container">
        <div className="hero-gradient-bg p-2">
          <div className="d-flex justify-content-between align-items-start">
            <span className="Industries-img d-flex gap-2 align-items-center pt-1">
              <FaBuilding size={20} style={{ color: '#053b84', marginRight: "4px" }} />
              <h4 className="fw-bold text-dark mb-0">Industries</h4>
            </span>
            {isFiltered && (
              <button
                className="btn btn-link text-decoration-none p-0 fw-bold pt-2 pe-2 transition"
                style={{ fontSize: "0.85rem", color: "#053b84" }}
                onClick={() => {
                  if (onClearAll) {
                    onClearAll();
                  } else {
                    if (onCategorySelect) onCategorySelect(null);
                    if (onSubCategorySelect) onSubCategorySelect(null);
                  }
                }}
              >
                Clear all
              </button>
            )}
          </div>
          <span
            className="fw-Regular text-dark mb-2 d-block"
            style={{ fontSize: "13.6px" }}
          >
            Filter reports by industry
          </span>
        </div>

        <div
          className="custom-scrollbar py-2"
          style={{ maxHeight: "335px", overflowY: "auto" }}
        >
          {visibleCategories.map((cat) => {
            const availCatsLower = availableCategories.map((c) =>
              c.toLowerCase().trim(),
            );
            const availSubCatsLower = availableSubCategories.map((c) =>
              c.toLowerCase().trim(),
            );

            const hasMainCategory = availCatsLower.includes(
              cat.name.toLowerCase().trim(),
            );
            const hasMatchingSubCategory = cat.subCategories.some((sub) =>
              availSubCatsLower.includes(sub.toLowerCase().trim()),
            );
            const isEmptySystem =
              availableCategories.length === 0 &&
              availableSubCategories.length === 0;

            if (!isEmptySystem && !hasMainCategory && !hasMatchingSubCategory)
              return null;
            if (!isEmptySystem && getCategoryCount(cat.name) === 0) return null;
            if (
              isEmptySystem &&
              baseReports.length > 0 &&
              getCategoryCount(cat.name) === 0
            )
              return null;

            const validSubCategories = (
              isEmptySystem
                ? cat.subCategories
                : cat.subCategories.filter((sub) =>
                    availSubCatsLower.includes(sub.toLowerCase().trim()),
                  )
            ).filter(
              (sub) =>
                baseReports.length === 0 ||
                getSubCategoryCount(cat.name, sub) > 0,
            );

            const isOpen = openIds.includes(cat.id);
            const isActive = selectedCategory === cat.name;

            return (
              <div key={cat.id} className="mb-1">
                <div
                  className={`category-btn w-100 d-flex justify-content-between align-items-center cursor-pointer ${isActive ? "active" : ""}`}
                  style={{ textAlign: "left", padding: "1px 15px" }}
                >
                  <div
                    className="category-icon d-flex align-items-center gap-2 flex-grow-1"
                    onClick={() => handleCategoryFilter(cat)}
                  >
                    {/* Existing blurry icon commented out for senior approval
                                        <img src={cat.icon} alt={`${cat.name} industry icon`} style={{ objectFit: 'contain', flexShrink: 0, filter: cat.icon === iconOilGas ? 'opacity(0.6) grayscale(1)' : 'none' }} />
                                        */}
                    {(() => {
                      const IconComponent = getIndustryFaIcon(cat.name);
                      return (
                        <IconComponent
                          style={{
                            fontSize: "19px",
                            color: "#053b84",
                            flexShrink: 0,
                          }}
                          className="me-2"
                        />
                      );
                    })()}
                    <span
                      className="text-start fw-medium"
                      style={{ lineHeight: "1.4" }}
                    >
                      {cat.name}{" "}
                      <span
                        className="text-secondary fw-normal opacity-75 ms-1"
                        style={{ fontSize: "0.9rem" }}
                      >
                        ({getCategoryCount(cat.name)})
                      </span>
                    </span>
                  </div>
                  <span
                    className="small text-muted flex-shrink-0 ms-2 p-1"
                    onClick={(e) => handleToggleAccordion(e, cat)}
                    style={{ cursor: "pointer" }}
                    aria-label={`${isOpen ? "Collapse" : "Expand"} ${cat.name} subcategories`}
                    aria-expanded={isOpen}
                    role="button"
                  >
                    {isOpen ? (
                      <FaChevronUp size={10} />
                    ) : (
                      <FaChevronDown size={10} />
                    )}
                  </span>
                </div>

                {isOpen && validSubCategories.length > 0 && (
                  <ul className="subcategory-list mt-0">
                    {validSubCategories.map((sub) => (
                      <li
                        key={sub}
                        className={`subcategory-item d-flex justify-content-between align-items-center ${selectedSubCategory === sub ? "active" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSubCategorySelect(sub);
                        }}
                        style={{
                          padding: "5px 14px 5px 58px",
                          fontSize: "0.85rem",
                        }}
                      >
                        <span>{sub}</span>
                        <span
                          className="text-secondary opacity-75 fw-normal"
                          style={{ fontSize: "0.75rem" }}
                        >
                          ({getSubCategoryCount(cat.name, sub)})
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
};

export default Categories;
